'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface FormData {
  companyName: string;
  companyType: string;
  contactName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

interface Props {
  onSwitchToLogin: () => void;
}

const companyTypes = [
  'Mechanical & Electrical (M&E)',
  'HVAC Contractor',
  'Electrical Contractor',
  'Plumbing & Heating',
  'Fire & Security',
  'Gas Engineering',
  'Civil & Structural',
  'Multi-discipline Engineering',
  'Facilities Management',
  'Other Engineering',
];

export default function SignUpForm({ onSwitchToLogin }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { signUp } = useAuth();
  const router = useRouter();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>();

  const password = watch('password');

  const onSubmit = async (data: FormData) => {
    try {
      await signUp(data.email, data.password, {
        full_name: data.contactName,
        phone: data.phone,
        company_name: data.companyName,
        company_type: data.companyType,
        role: 'manager',
      });
      toast.success('Account created — let\'s set up your workspace');
      router.push('/onboarding');
    } catch (err: any) {
      const msg = err?.message ?? 'Registration failed';
      if (msg.toLowerCase().includes('already registered') || msg.toLowerCase().includes('already exists')) {
        toast.error('An account with this email already exists. Please sign in instead.');
      } else {
        toast.error(msg);
      }
    }
  };

  const inputClass =
    'w-full px-3.5 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400';
  const labelClass = 'block text-xs font-600 text-gray-600 uppercase tracking-wide mb-1.5';

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-[1.4rem] font-700 text-gray-900 tracking-tight">Create your account</h2>
        <p className="text-sm text-gray-500 mt-1">Register your engineering company</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Company section */}
        <div>
          <p className="text-[10px] font-700 uppercase tracking-widest text-gray-400 mb-3">Company Details</p>
          <div className="space-y-3">
            <div>
              <label className={labelClass}>Company Name <span className="text-red-500 normal-case">*</span></label>
              <input
                {...register('companyName', {
                  required: 'Company name is required',
                  minLength: { value: 2, message: 'Must be at least 2 characters' },
                })}
                className={inputClass}
                placeholder="e.g. Meridian Engineering Ltd"
              />
              {errors.companyName && <p className="text-xs text-red-500 mt-1.5">{errors.companyName.message}</p>}
            </div>

            <div>
              <label className={labelClass}>Company Type <span className="text-red-500 normal-case">*</span></label>
              <select
                {...register('companyType', { required: 'Company type is required' })}
                className={inputClass}
              >
                <option value="">Select company type...</option>
                {companyTypes.map((t) => (
                  <option key={`ct-${t.slice(0, 10)}`} value={t}>{t}</option>
                ))}
              </select>
              {errors.companyType && <p className="text-xs text-red-500 mt-1.5">{errors.companyType.message}</p>}
            </div>
          </div>
        </div>

        {/* Contact section */}
        <div>
          <p className="text-[10px] font-700 uppercase tracking-widest text-gray-400 mb-3">Your Details</p>
          <div className="space-y-3">
            <div>
              <label className={labelClass}>Full Name <span className="text-red-500 normal-case">*</span></label>
              <input
                {...register('contactName', { required: 'Full name is required' })}
                className={inputClass}
                placeholder="e.g. Ross Hadley"
              />
              {errors.contactName && <p className="text-xs text-red-500 mt-1.5">{errors.contactName.message}</p>}
            </div>

            <div>
              <label className={labelClass}>Work Email <span className="text-red-500 normal-case">*</span></label>
              <input
                type="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                })}
                className={inputClass}
                placeholder="you@yourcompany.co.uk"
                autoComplete="email"
              />
              {errors.email && <p className="text-xs text-red-500 mt-1.5">{errors.email.message}</p>}
            </div>

            <div>
              <label className={labelClass}>Phone Number</label>
              <input
                type="tel"
                {...register('phone')}
                className={inputClass}
                placeholder="e.g. 07700 900000"
              />
            </div>
          </div>
        </div>

        {/* Password section */}
        <div>
          <p className="text-[10px] font-700 uppercase tracking-widest text-gray-400 mb-3">Set Password</p>
          <div className="space-y-3">
            <div>
              <label className={labelClass}>Password <span className="text-red-500 normal-case">*</span></label>
              <p className="text-xs text-gray-400 mb-1.5">Min. 8 characters with a number and symbol</p>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 8, message: 'At least 8 characters required' },
                    pattern: {
                      value: /^(?=.*[0-9])(?=.*[!@#$%^&*])/,
                      message: 'Include at least one number and one symbol',
                    },
                  })}
                  className={`${inputClass} pr-10`}
                  placeholder="Create a strong password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-500 mt-1.5">{errors.password.message}</p>}
            </div>

            <div>
              <label className={labelClass}>Confirm Password <span className="text-red-500 normal-case">*</span></label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (value) => value === password || 'Passwords do not match',
                  })}
                  className={`${inputClass} pr-10`}
                  placeholder="Repeat your password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
                >
                  {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-xs text-red-500 mt-1.5">{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Terms */}
        <div className="flex items-start gap-2.5 pt-1">
          <input
            type="checkbox"
            id="terms"
            {...register('terms', { required: 'You must accept the terms to continue' })}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500/30 mt-0.5 flex-shrink-0 accent-blue-600"
          />
          <label htmlFor="terms" className="text-sm text-gray-500 cursor-pointer leading-snug">
            I agree to the{' '}
            <Link href="#" className="text-blue-600 hover:text-blue-700 font-500">Terms of Service</Link>{' '}
            and{' '}
            <Link href="#" className="text-blue-600 hover:text-blue-700 font-500">Privacy Policy</Link>.
            I confirm I am authorised to register my company.
          </label>
        </div>
        {errors.terms && <p className="text-xs text-red-500 -mt-3">{errors.terms.message}</p>}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-600 text-white rounded-lg transition-all duration-200 disabled:opacity-60"
          style={{
            background: isSubmitting
              ? '#6b7280' :'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
            boxShadow: isSubmitting ? 'none' : '0 2px 12px rgba(29,78,216,0.35)',
          }}
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Creating account...
            </>
          ) : (
            <>
              Create Company Account
              <ArrowRight size={15} />
            </>
          )}
        </button>
      </form>

      <div className="mt-5 pt-5 border-t border-gray-100 text-center">
        <p className="text-sm text-gray-500">
          Already have an account?{' '}
          <button onClick={onSwitchToLogin} className="text-blue-600 font-600 hover:text-blue-700 transition-colors">
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
}